import { initClient, type ApiFetcherArgs } from "@ts-rest/core";
import {
  assetContract,
  categoryContract,
  contract,
  dashboardContract,
  facilityContract,
  favoriteContract,
  kioskContract,
  manualContract,
  manualLockContract,
  mediaJobContract,
  pmBoardContract,
  pmDrehbuchContract,
  pmTaskContract,
  pmTaskMediaContract,
  pmVisitContract,
  roleContract,
  sensorContract,
  taskBoardContract,
  type AssetUploadResult,
  type ManualBody,
  type PmTaskMedia,
} from "@arc/contracts";
import { cachedAccessToken, getAccessToken } from "../auth/keycloak.js";

/**
 * Typed API client. The base URL is RELATIVE so every call goes to the tenant's OWN origin — same-origin (no CORS)
 * and the Host tenant signal survives (an absolute http://localhost:8080 would break both).
 *
 * `api` is the auth transport SEAM: today it is a plain fetch, and in the auth phase it becomes the single place
 * that awaits a coalesced `updateToken()` and attaches the bearer — so token handling never leaks into components.
 */
const configuredBase = import.meta.env.VITE_API_BASE_URL ?? "/svc.ar-creator.test/v1";

// Must be a SINGLE-leading-slash relative path. An absolute ("http://localhost:8080/...") or protocol-relative
// ("//host/...") value would send every request cross-origin — dropping the Host tenant signal and breaking CORS —
// which is precisely the misconfiguration this base was made relative to avoid. Fail fast and loudly at startup.
if (!/^\/(?!\/)/.test(configuredBase)) {
  throw new Error(
    `VITE_API_BASE_URL must be a relative path starting with "/" (got ${JSON.stringify(configuredBase)}). ` +
      "The UI must call the API on its own tenant origin.",
  );
}
const baseUrl = configuredBase;

/**
 * ONE transport = ONE auth seam, shared by every typed client. Adding a contract means another `initClient(…, transport)`
 * — never a second copy of the token logic. `api` is that seam: today a plain fetch that attaches the coalesced bearer;
 * in the auth phase it stays the single place tokens are handled, so they never leak into components/logs/query keys.
 */
const transport = {
  baseUrl,
  baseHeaders: {},
  api: async (args: ApiFetcherArgs) => {
    const token = await getAccessToken();
    const headers: Record<string, string> = { ...args.headers };
    if (token) headers["authorization"] = `Bearer ${token}`;

    const response = await fetch(args.path, {
      method: args.method,
      headers,
      body: args.body,
      credentials: "same-origin",
    });
    const contentType = response.headers.get("content-type") ?? "";
    // `response.json()` is typed `any`; the cast keeps that from flowing into the client and erasing the end-to-end
    // type safety the contract exists to provide. `.text()` is already `string`.
    const body: unknown = contentType.includes("json") ? ((await response.json()) as unknown) : await response.text();
    return { status: response.status, body, headers: response.headers };
  },
} as const;

export const apiClient = initClient(contract, transport);
export const facilityApi = initClient(facilityContract, transport);
export const categoryApi = initClient(categoryContract, transport);
export const manualApi = initClient(manualContract, transport);
export const favoriteApi = initClient(favoriteContract, transport);
export const assetApi = initClient(assetContract, transport);
export const manualLockApi = initClient(manualLockContract, transport);
export const mediaJobApi = initClient(mediaJobContract, transport);
export const roleApi = initClient(roleContract, transport);
/** Sensors (ADR-035): the SuperAdmin-only Manage sensors page — read + soft-delete. */
export const sensorApi = initClient(sensorContract, transport);
/** Kiosk profiles (ADR-036): the SuperAdmin setup + the device's own `/kiosk/me` read. */
export const kioskApi = initClient(kioskContract, transport);
export const dashboardApi = initClient(dashboardContract, transport);
/** The AFZ project-management board at `/pm` (ADR-055) — reads + admin, tasks + annotations, media, the Drehbuch. */
export const pmBoardApi = initClient(pmBoardContract, transport);
export const pmTaskApi = initClient(pmTaskContract, transport);
export const pmTaskMediaApi = initClient(pmTaskMediaContract, transport);
export const pmDrehbuchApi = initClient(pmDrehbuchContract, transport);
export const pmVisitApi = initClient(pmVisitContract, transport);
/** The task board at `/task-board`: the page reads, the MCP tools write. */
export const taskBoardApi = initClient(taskBoardContract, transport);

/**
 * Release the edit lease during page UNLOAD (ADR-026 Phase 4).
 *
 * A normal request is cancelled when the page goes away, so the manual would stay locked until its TTL lapsed — the
 * stale-lock problem the legacy papered over with a 2-minute lease (it wrote exactly such a beacon and then never
 * called it). `keepalive` lets the request outlive the document.
 *
 * It lives HERE rather than in the editor because this module is the single auth seam: the bearer is attached in one
 * place and never reaches a component. `sendBeacon` is not usable — it cannot carry an Authorization header.
 */
export function releaseManualLockBeacon(manualId: number, body: { clientSessionId: string; leaseId: string }): void {
  // **Synchronous by necessity (diff-panel R6-F5).** `keepalive` only protects a request the unload handler issues
  // synchronously; awaiting a token refresh here pushed the fetch into a microtask that page teardown often never runs,
  // so the release was frequently dropped and the manual stayed locked for the full TTL. `cachedAccessToken()` is the
  // last known token — good enough, since this fires seconds after a live, renewed session.
  const token = cachedAccessToken();
  void fetch(`${baseUrl}/manuals/${manualId}/lock`, {
    method: "DELETE",
    keepalive: true,
    credentials: "same-origin",
    headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  }).catch(() => undefined);
}

/**
 * Streaming asset upload (ADR-025 A2). ts-rest can't model `multipart/form-data`, so this hand-builds the FormData POST — but
 * THROUGH THE SAME AUTH SEAM (the relative `baseUrl` on the tenant origin + the coalesced bearer), so token handling still
 * never leaks into components. `folderId` null ⇒ the realm's Master root. Resolves the 202 `AssetUploadResult`; throws the
 * server's error message otherwise. (Indeterminate progress only — `fetch` can't report upload progress; a determinate
 * XHR-based bar is a follow-up.)
 */
export async function uploadAssetFile(file: File, folderId: number | null): Promise<AssetUploadResult> {
  const token = await getAccessToken();
  const form = new FormData();
  form.append("file", file, file.name);
  const qs = folderId != null ? `?folderId=${encodeURIComponent(folderId)}` : "";
  const res = await fetch(`${baseUrl}/assets/files${qs}`, {
    method: "POST",
    headers: token ? { authorization: `Bearer ${token}` } : {},
    body: form,
    credentials: "same-origin",
  });
  return unwrapUpload(res);
}

/**
 * "This manual" (project files, #217 P2) streaming upload — the manual-scoped twin of {@link uploadAssetFile}. Same
 * hand-built multipart POST on the same auth seam, but to `POST /manuals/:id/assets/files`, so the server authorizes through
 * the manual's OWN edit gate + lands the file in the manual's own folder (lazy-creating it on first upload). `folderId` null
 * ⇒ the manual's project-folder root.
 */
export async function uploadManualAssetFile(manualId: number, file: File, folderId: number | null): Promise<AssetUploadResult> {
  const token = await getAccessToken();
  const form = new FormData();
  form.append("file", file, file.name);
  const qs = folderId != null ? `?folderId=${encodeURIComponent(folderId)}` : "";
  const res = await fetch(`${baseUrl}/manuals/${manualId}/assets/files${qs}`, {
    method: "POST",
    headers: token ? { authorization: `Bearer ${token}` } : {},
    body: form,
    credentials: "same-origin",
  });
  return unwrapUpload(res);
}

/** Shared 202-or-throw handling for the two streaming asset uploads (Master + This-manual). */
async function unwrapUpload(res: Response): Promise<AssetUploadResult> {
  if (res.status !== 202) {
    let message = `upload failed (${res.status})`;
    try {
      const b = (await res.json()) as { message?: string };
      if (typeof b?.message === "string") message = b.message;
    } catch {
      /* non-JSON error body → keep the default message */
    }
    throw new Error(message);
  }
  return (await res.json()) as AssetUploadResult;
}

/**
 * Save an edited step image straight onto the step (ADR-025 A3a edit-attach). Like {@link uploadAssetFile}, ts-rest can't
 * model `multipart/form-data`, so this hand-builds the FormData POST — but THROUGH THE SAME AUTH SEAM (the relative `baseUrl`
 * on the tenant origin + the coalesced bearer), so token handling never leaks into components. The server validates the PNG,
 * lands it copy-on-write, CASes on `expectedVersion` (409 `MANUAL_BODY_STALE` on a stale token), and returns the updated
 * `ManualBody` — the same shape every body-write mutation returns. The thrown error carries the HTTP `status` + the contract
 * `code` (`IMAGE_INVALID` / `MANUAL_BODY_STALE` / …) so the editor can show inline copy.
 */
export async function saveEditedStepImage(manualId: number, stepId: number, file: File, expectedVersion: number): Promise<ManualBody> {
  const token = await getAccessToken();
  const form = new FormData();
  form.append("file", file, file.name);
  const res = await fetch(`${baseUrl}/manuals/${manualId}/steps/${stepId}/media/edited?expectedVersion=${encodeURIComponent(expectedVersion)}`, {
    method: "POST",
    headers: token ? { authorization: `Bearer ${token}` } : {},
    body: form,
    credentials: "same-origin",
  });
  if (res.status !== 200) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* non-JSON error body */
    }
    const err = new Error((body as { message?: string } | null)?.message ?? `save failed (${res.status})`) as Error & { status: number; code?: string; body?: unknown };
    err.status = res.status;
    err.code = (body as { code?: string } | null)?.code;
    err.body = body;
    throw err;
  }
  return (await res.json()) as ManualBody;
}

/**
 * Attach one file to a PM-board task (ADR-055 phase 3b). Like the asset upload, a hand-built multipart POST through the
 * same auth seam. The server checks the type against the file's own first bytes and caps it at 500 MB; the thrown error
 * carries `status` + `code` (`PM_MEDIA_TYPE` / `PM_MEDIA_TOO_LARGE` / …) so the dialog can say which file failed and why.
 */
export async function uploadPmTaskMedia(taskId: number, file: File): Promise<PmTaskMedia> {
  const token = await getAccessToken();
  const form = new FormData();
  form.append("file", file, file.name);
  const res = await fetch(`${baseUrl}/pm/tasks/${taskId}/media`, {
    method: "POST",
    headers: token ? { authorization: `Bearer ${token}` } : {},
    body: form,
    credentials: "same-origin",
  });
  if (res.status !== 201) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* non-JSON error body */
    }
    const err = new Error((body as { message?: string } | null)?.message ?? `upload failed (${res.status})`) as Error & { status: number; code?: string };
    err.status = res.status;
    err.code = (body as { code?: string } | null)?.code;
    throw err;
  }
  return (await res.json()) as PmTaskMedia;
}
